import React, { memo } from 'react';
import clsx from 'clsx';
import { useContextMenu } from '../contexts/ContextMenuContext';
import { copyToClipboard } from '../utils/copyToClipboard';
import ImmutableRenderer from './ImmutableRenderer';

interface StateSectionProps {
    sectionKey: string;
    sectionValue: any;
    isExpanded: boolean;
    isMatch: boolean;
    onToggle: (key: string) => void;
    searchPath: string | null;
    searchResults: Record<string, boolean>;
    matchingPaths?: string[];
    onHoverPath?: (path: string) => void;
}

/**
 * Component for rendering a top-level section of the Redux state
 * @param props - Component props
 * @returns Rendered component
 */
const StateSection = memo(
    ({
        sectionKey,
        sectionValue,
        isExpanded,
        isMatch,
        onToggle,
        searchPath,
        searchResults,
        matchingPaths = [],
        onHoverPath,
    }: StateSectionProps) => {
        const { openContextMenu } = useContextMenu();

        // Handle right click to show context menu
        const handleContextMenu = (e: React.MouseEvent) => {
            e.preventDefault();
            e.stopPropagation();

            openContextMenu({
                x: e.clientX,
                y: e.clientY,
                menuItems: [
                    {
                        label: 'Copy Section',
                        action: () => {
                            const result: Record<string, any> = {};
                            result[sectionKey] = sectionValue;
                            copyToClipboard(result);
                        },
                    },
                    {
                        label: 'Expand Section',
                        action: () => onToggle(sectionKey),
                    },
                ],
            });
        };

        return (
            <div
                className="mb-2 border-b pb-2"
                onContextMenu={handleContextMenu}
            >
                <h3
                    onClick={() => onToggle(sectionKey)}
                    className={clsx(
                        'cursor-pointer font-bold hover:text-gray-600',
                        isMatch && 'text-green',
                    )}
                    onMouseEnter={() => onHoverPath && onHoverPath(sectionKey)}
                    onMouseLeave={() => onHoverPath && onHoverPath('')}
                >
                    {isExpanded ? '🗃️ ' : '🗄️ '}
                    {sectionKey}
                </h3>
                {isExpanded && (
                    <div className="mt-1 pl-2">
                        <ImmutableRenderer
                            data={sectionValue}
                            searchPath={searchPath}
                            currentPath={sectionKey}
                            expandedPaths={searchResults}
                            matchingPaths={matchingPaths}
                            onHoverPath={(path) =>
                                onHoverPath &&
                                onHoverPath(path ? path : sectionKey)
                            }
                        />
                    </div>
                )}
            </div>
        );
    },
);

export default StateSection;
